/*
<purpose>
  LAZY MSAL (Entra ID) singleton for the SPA.
</purpose>
<dependencies>@azure/msal-browser (PublicClientApplication). process.env.REACT_APP_ENTRA_CLIENT_ID,
REACT_APP_ENTRA_AUTHORITY, REACT_APP_FA_API_SCOPE — build-time CRA env vars, same injection
path as versionInfo.ts.</dependencies>
<constraints>
  LAZY ON PURPOSE — construction and env validation happen at first call, never at module load,
  so a REACT_APP_SKIP_AUTH=true local build never demands the three Entra values (index.tsx skips
  the bootstrap under that switch).

  cacheLocation is 'sessionStorage' — a hard ruling, never localStorage (persists across
  tabs/after close) or memoryStorage (drops the session on refresh). @azure/msal-browser v5:
  msalInstance.initialize() MUST be awaited before any other MSAL API call — the caller that
  bootstraps the render path (index.tsx) owns that call.
</constraints>
*/

import { PublicClientApplication } from '@azure/msal-browser';

// Why validated here rather than non-null-asserted: a missing build value would otherwise
// surface as an opaque MSAL error at first token acquisition. Name the exact variable instead.
const requireEnv = (name: string): string => {
  const value = process.env[name];
  if (value === undefined || value === '') {
    throw new Error(
      `${name} is not set — the build was produced without its Entra configuration. The deploy `
      + 'script injects all three (REACT_APP_ENTRA_CLIENT_ID, REACT_APP_ENTRA_AUTHORITY, '
      + 'REACT_APP_FA_API_SCOPE); a hand-rolled build must supply them, or set '
      + 'REACT_APP_SKIP_AUTH=true for local dev.'
    );
  }
  return value;
};

let instance: PublicClientApplication | null = null;

export const getMsalInstance = (): PublicClientApplication => {
  if (instance === null) {
    instance = new PublicClientApplication({
      auth: {
        clientId: requireEnv('REACT_APP_ENTRA_CLIENT_ID'),
        authority: requireEnv('REACT_APP_ENTRA_AUTHORITY'),
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    });
  }
  return instance;
};

// Note the audience asymmetry (measured, both correct): the backend validates the BARE
// application id as `aud`, while the value a client requests is the api://<appId>/... scope.
// They are different strings; "harmonizing" one into the other breaks token acquisition.
export const getFaTokenRequest = (): { scopes: string[] } => ({
  scopes: [requireEnv('REACT_APP_FA_API_SCOPE')],
});
